import { id } from '@instantdb/react'
import { db } from './db'
import { isAdminEmail } from './adminEmails'
import { DEFAULT_PACKAGES, termToDays } from './packageCatalog'
import {
  mapPackage,
  mapPreference,
  mapRecommendation,
  mapSession,
  mapUserPackage,
  Package,
  TrainingSession,
  UserPackage,
  UserPreference,
  ClientRecommendation,
} from './types'

export async function fetchAllPreferences(): Promise<UserPreference[]> {
  const { data } = await db.queryOnce({ userPreferences: {} })
  return (data.userPreferences || [])
    .map(mapPreference)
    .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))
}

export async function fetchPreferenceByUserId(
  userId: string
): Promise<UserPreference | null> {
  const { data } = await db.queryOnce({
    userPreferences: { $: { where: { userId } } },
  })
  const row = data.userPreferences?.[0]
  return row ? mapPreference(row) : null
}

export async function upsertPreference(
  userId: string,
  fields: {
    email: string
    fullName: string
    phone?: string
    location?: string
    dateOfBirth?: string
    fitnessGoals?: string[]
    workoutTime?: string
    avatarUrl?: string
    notificationPreferences?: Record<string, boolean>
  }
) {
  const { data: existing } = await db.queryOnce({
    userPreferences: { $: { where: { userId } } },
  })
  const now = Date.now()
  if (existing.userPreferences?.[0]) {
    await db.transact(
      db.tx.userPreferences[existing.userPreferences[0].id].update({
        ...fields,
        updatedAt: now,
      })
    )
    return existing.userPreferences[0].id
  }
  const prefId = id()
  await db.transact(
    db.tx.userPreferences[prefId]
      .update({
        userId,
        ...fields,
        createdAt: now,
        updatedAt: now,
      })
      .link({ user: userId })
  )
  return prefId
}

export async function deletePreference(preferenceId: string) {
  await db.transact(db.tx.userPreferences[preferenceId].delete())
}

export async function fetchPackages(activeOnly = false): Promise<Package[]> {
  const { data } = await db.queryOnce({ packages: {} })
  let rows = (data.packages || []).map(mapPackage)
  if (activeOnly) rows = rows.filter((p) => p.is_active)
  return rows.sort((a, b) => a.name.localeCompare(b.name))
}

export async function createPackage(input: {
  name: string
  description?: string
  price: number
  durationDays: number
  features?: string[]
  packageType: string
  isActive?: boolean
  sessionsIncluded?: number
}) {
  const packageId = id()
  await db.transact(
    db.tx.packages[packageId].update({
      name: input.name,
      description: input.description || '',
      price: input.price,
      durationDays: input.durationDays,
      features: input.features || [],
      packageType: input.packageType,
      isActive: input.isActive ?? true,
      sessionsIncluded: input.sessionsIncluded,
      createdAt: Date.now(),
    })
  )
  return packageId
}

/**
 * Creates any of Cassie's standard programs that aren't in the database yet.
 * Safe to run repeatedly — matching is by name, so edited packages are left alone.
 */
export async function seedDefaultPackages(): Promise<number> {
  const existing = await fetchPackages(false)
  const existingNames = new Set(
    existing.map((pkg) => pkg.name.trim().toLowerCase())
  )
  const missing = DEFAULT_PACKAGES.filter(
    (pkg) => !existingNames.has(pkg.name.trim().toLowerCase())
  )
  if (missing.length === 0) return 0

  const now = Date.now()
  await db.transact(
    missing.map((pkg) =>
      db.tx.packages[id()].update({
        name: pkg.name,
        description: pkg.description,
        price: pkg.price,
        durationDays: termToDays(pkg.terms[0]),
        features: pkg.features,
        packageType: pkg.packageType,
        isActive: true,
        sessionsIncluded: pkg.sessionsIncluded,
        createdAt: now,
      })
    )
  )
  return missing.length
}

export async function updatePackage(packageId: string, patch: Record<string, any>) {
  await db.transact(db.tx.packages[packageId].update(patch))
}

export async function deletePackage(packageId: string) {
  await db.transact(db.tx.packages[packageId].delete())
}

export async function fetchUserPackages(userId?: string): Promise<UserPackage[]> {
  const { data } = await db.queryOnce({
    userPackages: { package: {} },
  })
  const rows = (data.userPackages || []) as any[]
  const filtered = userId ? rows.filter((r) => r.userId === userId) : rows
  return filtered.map(mapUserPackage)
}

export async function assignUserPackage(input: {
  userId: string
  packageId: string
  packageName?: string
  packageType?: string
  status?: string
  purchaseDate?: number
  expiryDate?: number
  sessionsRemaining?: number
}) {
  const rowId = id()
  await db.transact(
    db.tx.userPackages[rowId]
      .update({
        userId: input.userId,
        packageId: input.packageId,
        packageName: input.packageName,
        packageType: input.packageType,
        status: input.status || 'active',
        purchaseDate: input.purchaseDate || Date.now(),
        expiryDate: input.expiryDate,
        sessionsRemaining: input.sessionsRemaining,
        createdAt: Date.now(),
      })
      .link({ user: input.userId, package: input.packageId })
  )
  return rowId
}

/**
 * Admin-only patch of a single userPackages row — used for the Progress
 * Tracker link/type and the Training Guide / Competition Prep entitlement
 * flags. Perms already restrict userPackages.update to Cassie's account.
 */
export async function updateUserPackage(
  packageRowId: string,
  patch: {
    trackerUrl?: string
    trackerType?: string
    includesTraining?: boolean
    competitionPrep?: boolean
  }
) {
  await db.transact(db.tx.userPackages[packageRowId].update(patch))
}

export async function fetchSessions(filters?: {
  userId?: string
}): Promise<TrainingSession[]> {
  const { data } = await db.queryOnce({ trainingSessions: {} })
  const rows = (data.trainingSessions || []) as any[]
  const filtered = filters?.userId
    ? rows.filter((r) => r.userId === filters.userId)
    : rows
  return filtered.map(mapSession).sort((a, b) => b.scheduled_date.localeCompare(a.scheduled_date))
}

export async function createSession(input: {
  userId: string
  trainerId?: string
  packageId?: string
  scheduledDate: number | string
  durationMinutes: number
  sessionType: string
  status?: string
  notes?: string
  mealPlanWeeks?: number
  pickupDeliveryInfo?: string
  clientFullName?: string
  clientEmail?: string
}) {
  const sessionId = id()
  const scheduled =
    typeof input.scheduledDate === 'number'
      ? input.scheduledDate
      : Date.parse(input.scheduledDate)
  await db.transact(
    db.tx.trainingSessions[sessionId]
      .update({
        userId: input.userId,
        trainerId: input.trainerId,
        packageId: input.packageId,
        scheduledDate: scheduled,
        durationMinutes: input.durationMinutes,
        sessionType: input.sessionType,
        status: input.status || 'scheduled',
        notes: input.notes,
        mealPlanWeeks: input.mealPlanWeeks,
        pickupDeliveryInfo: input.pickupDeliveryInfo,
        clientFullName: input.clientFullName,
        clientEmail: input.clientEmail,
        createdAt: Date.now(),
        updatedAt: Date.now(),
      })
      .link({ user: input.userId })
  )
  return sessionId
}

export async function deleteSession(sessionId: string) {
  await db.transact(db.tx.trainingSessions[sessionId].delete())
}

export async function updateSession(sessionId: string, patch: Record<string, any>) {
  await db.transact(
    db.tx.trainingSessions[sessionId].update({
      ...patch,
      updatedAt: Date.now(),
    })
  )
}

export async function fetchRecommendation(
  userId: string
): Promise<ClientRecommendation | null> {
  const { data } = await db.queryOnce({
    clientRecommendations: { $: { where: { userId } } },
  })
  const row = data.clientRecommendations?.[0]
  return row ? mapRecommendation(row) : null
}

export async function upsertRecommendation(
  userId: string,
  fields: {
    nonTrainingDayPlan?: string[]
    personalNote?: string
    iconColors?: string[]
  }
) {
  const { data: existing } = await db.queryOnce({
    clientRecommendations: { $: { where: { userId } } },
  })
  const now = Date.now()
  if (existing.clientRecommendations?.[0]) {
    await db.transact(
      db.tx.clientRecommendations[existing.clientRecommendations[0].id].update({
        ...fields,
        updatedAt: now,
      })
    )
    return existing.clientRecommendations[0].id
  }
  const recId = id()
  await db.transact(
    db.tx.clientRecommendations[recId]
      .update({
        userId,
        ...fields,
        createdAt: now,
        updatedAt: now,
      })
      .link({ user: userId })
  )
  return recId
}

export async function fetchChatMessages() {
  const { data } = await db.queryOnce({ chatMessages: {} })
  return (data.chatMessages || []).sort(
    (a: any, b: any) => Number(a.createdAt) - Number(b.createdAt)
  )
}

export async function insertChatMessage(input: {
  userId: string
  userName: string
  content: string
  messageType: string
  fileUrl?: string
}) {
  const messageId = id()
  await db.transact(
    db.tx.chatMessages[messageId]
      .update({
        userId: input.userId,
        userName: input.userName,
        content: input.content,
        messageType: input.messageType,
        fileUrl: input.fileUrl,
        createdAt: Date.now(),
        updatedAt: Date.now(),
      })
      .link({ user: input.userId })
  )
  return messageId
}

export async function updateChatMessage(messageId: string, content: string) {
  await db.transact(
    db.tx.chatMessages[messageId].update({ content, updatedAt: Date.now() })
  )
}

export async function deleteChatMessage(messageId: string) {
  await db.transact(db.tx.chatMessages[messageId].delete())
}
export async function createCheckInRequest(input: {
  userId: string
  clientFullName: string
  clientEmail: string
  topic: string
  message: string
  preferredResponse: string
}) {
  const requestId = id()
  await db.transact(
    db.tx.checkInRequests[requestId].update({
      userId: input.userId,
      clientFullName: input.clientFullName,
      clientEmail: input.clientEmail,
      topic: input.topic,
      message: input.message,
      preferredResponse: input.preferredResponse,
      status: 'new',
      createdAt: Date.now(),
      updatedAt: Date.now(),
    })
  )
  return requestId
}

export async function fetchCheckInRequests() {
  const { data } = await db.queryOnce({ checkInRequests: {} })
  return (data.checkInRequests || []).sort(
    (a: any, b: any) => Number(b.createdAt) - Number(a.createdAt)
  )
}

// ---------------------------------------------------------------------------
// Message Cassie — private 1-on-1 coaching messages between a client and Cassie
// ---------------------------------------------------------------------------

export interface ClientMessageRow {
  id: string
  userId: string
  senderId: string
  senderName: string
  senderRole: 'client' | 'coach'
  content: string
  createdAt: number
  readByClient: boolean
  readByCoach: boolean
}

function mapClientMessage(row: any): ClientMessageRow {
  return {
    id: row.id,
    userId: row.userId,
    senderId: row.senderId,
    senderName: row.senderName,
    senderRole: row.senderRole,
    content: row.content,
    createdAt: Number(row.createdAt) || 0,
    readByClient: !!row.readByClient,
    readByCoach: !!row.readByCoach,
  }
}

/** Client-side: fetch the signed-in client's own thread with Cassie. */
export async function fetchClientMessages(userId: string): Promise<ClientMessageRow[]> {
  const { data } = await db.queryOnce({
    clientMessages: { $: { where: { userId } } },
  })
  return (data.clientMessages || [])
    .map(mapClientMessage)
    .sort((a, b) => a.createdAt - b.createdAt)
}

/** Client sends a message to Cassie. */
export async function sendClientMessage(input: {
  userId: string
  senderName: string
  content: string
}) {
  const messageId = id()
  await db.transact(
    db.tx.clientMessages[messageId]
      .update({
        userId: input.userId,
        senderId: input.userId,
        senderName: input.senderName,
        senderRole: 'client',
        content: input.content,
        createdAt: Date.now(),
        readByClient: true,
        readByCoach: false,
      })
      .link({ user: input.userId })
  )
  return messageId
}

/** Client marks Cassie's replies as read. */
export async function markClientMessagesReadByClient(userId: string) {
  const { data } = await db.queryOnce({ clientMessages: { $: { where: { userId } } } })
  const unread = (data.clientMessages || []).filter((m: any) => !m.readByClient)
  if (!unread.length) return
  await db.transact(
    unread.map((m: any) => db.tx.clientMessages[m.id].update({ readByClient: true }))
  )
}

/**
 * Admin-side Message Cassie calls go through /api/admin/client-messages
 * (a server-side function) instead of calling adminDb directly from the
 * browser. This keeps the InstantDB admin token server-only. `token` is the
 * signed-in admin's own InstantDB refresh token (from `db.useAuth()`),
 * which the server verifies to confirm the caller is logged in as an
 * approved admin email — it carries no special access on its own.
 */
async function adminMessagesRequest(token: string, init?: RequestInit & { query?: string }) {
  const url = `/api/admin/client-messages${init?.query ? `?${init.query}` : ''}`
  const res = await fetch(url, {
    ...init,
    headers: {
      ...(init?.headers || {}),
      Authorization: `Bearer ${token}`,
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
    },
  })
  const body = await res.json().catch(() => ({}))
  if (!res.ok) {
    throw new Error(body?.error || `Request failed (${res.status})`)
  }
  return body
}

/** Admin: fetch one client's full thread. */
export async function fetchClientMessagesAdmin(
  userId: string,
  token: string
): Promise<ClientMessageRow[]> {
  const body = await adminMessagesRequest(token, { query: `userId=${encodeURIComponent(userId)}` })
  return (body.messages || []) as ClientMessageRow[]
}

/** Admin: every client thread, most recently active first. */
export async function fetchAllClientMessageThreads(
  token: string
): Promise<
  { userId: string; messages: ClientMessageRow[]; lastMessage: ClientMessageRow; unreadCount: number }[]
> {
  const body = await adminMessagesRequest(token)
  return (body.threads || []) as {
    userId: string
    messages: ClientMessageRow[]
    lastMessage: ClientMessageRow
    unreadCount: number
  }[]
}

/** Admin (Cassie) replies to a client. */
export async function sendCoachMessage(
  input: { userId: string; senderName: string; content: string },
  token: string
) {
  const body = await adminMessagesRequest(token, {
    method: 'POST',
    body: JSON.stringify({ action: 'send', ...input }),
  })
  return body.messageId as string
}

/** Admin marks a client's messages as read (opening a thread). */
export async function markClientMessagesReadByCoach(userId: string, token: string) {
  await adminMessagesRequest(token, {
    method: 'POST',
    body: JSON.stringify({ action: 'markRead', userId }),
  })
}

// ---------------------------------------------------------------------------
// Weekly progress check-ins
// ---------------------------------------------------------------------------

export interface WeeklyCheckInRow {
  id: string
  userId: string
  programType: 'standard' | 'competition'
  weekStartDate: string
  trainingCompleted: number | null
  trainingGoal: number | null
  goalStatus: string
  reflection: string
  measurements: Record<string, string> | null
  /** Legacy field, no longer populated by new check-ins. Not used for display. */
  photoUrls: string[]
  /** $files `path` strings for this check-in's photos, resolved via the progress-photo proxy. */
  photoPaths: string[]
  coachFeedback: string
  status: 'submitted' | 'reviewed'
  createdAt: number
  updatedAt: number | null
}

function mapWeeklyCheckIn(row: any): WeeklyCheckInRow {
  return {
    id: row.id,
    userId: row.userId,
    programType: row.programType,
    weekStartDate: row.weekStartDate,
    trainingCompleted: row.trainingCompleted ?? null,
    trainingGoal: row.trainingGoal ?? null,
    goalStatus: row.goalStatus || '',
    reflection: row.reflection || '',
    measurements: row.measurements || null,
    photoUrls: row.photoUrls || [],
    photoPaths: row.photoPaths || [],
    coachFeedback: row.coachFeedback || '',
    status: row.status,
    createdAt: Number(row.createdAt) || 0,
    updatedAt: row.updatedAt ? Number(row.updatedAt) : null,
  }
}

/** Client-side: fetch the signed-in client's own check-in history. */
export async function fetchMyWeeklyCheckIns(userId: string): Promise<WeeklyCheckInRow[]> {
  const { data } = await db.queryOnce({
    weeklyCheckIns: { $: { where: { userId } } },
  })
  return (data.weeklyCheckIns || [])
    .map(mapWeeklyCheckIn)
    .sort((a, b) => b.createdAt - a.createdAt)
}

/**
 * Client uploads a progress photo. Uses the client SDK end-to-end.
 *
 * Returns the file's storage `path`, not a URL — this is what gets stored
 * in weeklyCheckIns.photoPaths and later resolved server-side by the
 * progress-photo proxy. The app itself never stores or displays a raw
 * signed URL for a progress photo from here on.
 *
 * One unavoidable, documented exception: InstantDB's own re-query after
 * upload (below) returns the file's row including its signed `url` field,
 * so that value does briefly exist in this browser tab's memory and in its
 * network tab for this one request — the same request the actual owner is
 * making, at the moment they're uploading their own photo. It's discarded
 * immediately and never read, stored, or sent anywhere. This is InstantDB's
 * own upload-confirmation response shape; there's no way to upload a file
 * via the client SDK without it.
 */
export async function uploadProgressPhoto(userId: string, file: File): Promise<string> {
  const path = `progress-photos/${userId}/${Date.now()}-${file.name}`
  await db.storage.uploadFile(path, file, { contentType: file.type })
  // Confirm the upload actually landed before reporting success — we don't
  // use the returned url, only the fact that a $files row now exists.
  await db.queryOnce({ $files: { $: { where: { path } } } })
  return path
}

/** Client submits a new weekly check-in. */
export async function submitWeeklyCheckIn(input: {
  userId: string
  programType: 'standard' | 'competition'
  weekStartDate: string
  trainingCompleted?: number
  trainingGoal?: number
  goalStatus?: string
  reflection?: string
  measurements?: Record<string, string>
  /** $files `path` strings, from uploadProgressPhoto(). Never raw URLs. */
  photoPaths?: string[]
}) {
  const checkInId = id()
  await db.transact(
    db.tx.weeklyCheckIns[checkInId]
      .update({
        userId: input.userId,
        programType: input.programType,
        weekStartDate: input.weekStartDate,
        trainingCompleted: input.trainingCompleted,
        trainingGoal: input.trainingGoal,
        goalStatus: input.goalStatus || '',
        reflection: input.reflection || '',
        measurements: input.measurements || null,
        photoPaths: input.photoPaths || [],
        status: 'submitted',
        createdAt: Date.now(),
      })
      .link({ user: input.userId })
  )
  return checkInId
}

/**
 * Admin-side weekly check-in calls go through /api/admin/weekly-checkins
 * (a server-side function) instead of calling adminDb directly from the
 * browser — same pattern as Message Cassie's admin calls, so the InstantDB
 * admin token never reaches the browser bundle.
 */
async function adminCheckInsRequest(token: string, init?: RequestInit & { query?: string }) {
  const url = `/api/admin/weekly-checkins${init?.query ? `?${init.query}` : ''}`
  const res = await fetch(url, {
    ...init,
    headers: {
      ...(init?.headers || {}),
      Authorization: `Bearer ${token}`,
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
    },
  })
  const body = await res.json().catch(() => ({}))
  if (!res.ok) {
    throw new Error(body?.error || `Request failed (${res.status})`)
  }
  return body
}

/** Admin: fetch one client's full check-in history. */
export async function fetchWeeklyCheckInsAdmin(
  userId: string,
  token: string
): Promise<WeeklyCheckInRow[]> {
  const body = await adminCheckInsRequest(token, { query: `userId=${encodeURIComponent(userId)}` })
  return (body.checkIns || []) as WeeklyCheckInRow[]
}

/** Admin: every client's check-ins, grouped, most recently submitted first. */
export async function fetchAllWeeklyCheckIns(
  token: string
): Promise<
  { userId: string; checkIns: WeeklyCheckInRow[]; latest: WeeklyCheckInRow; pendingCount: number }[]
> {
  const body = await adminCheckInsRequest(token)
  return (body.clients || []) as {
    userId: string
    checkIns: WeeklyCheckInRow[]
    latest: WeeklyCheckInRow
    pendingCount: number
  }[]
}

/** Admin (Cassie) saves feedback on a client's check-in. */
export async function saveCheckInFeedback(checkInId: string, coachFeedback: string, token: string) {
  await adminCheckInsRequest(token, {
    method: 'POST',
    body: JSON.stringify({ action: 'saveFeedback', checkInId, coachFeedback }),
  })
}

export async function fetchProfiles() {
  const { data } = await db.queryOnce({ profiles: {} })
  return data.profiles || []
}

export async function upsertProfileOnline(userId: string, isOnline: boolean) {
  const { data } = await db.queryOnce({
    profiles: { $: { where: { userId } } },
  })
  const now = Date.now()
  if (data.profiles?.[0]) {
    await db.transact(
      db.tx.profiles[data.profiles[0].id].update({
        isOnline,
        lastSeen: now,
        updatedAt: now,
      })
    )
    return
  }
  const profileId = id()
  await db.transact(
    db.tx.profiles[profileId]
      .update({
        userId,
        isOnline,
        lastSeen: now,
        createdAt: now,
        updatedAt: now,
      })
      .link({ user: userId })
  )
}

export async function uploadPublicFile(path: string, file: File) {
  const { data } = await db.storage.uploadFile(path, file, {
    contentType: file.type,
  })
  // Re-query for URL — ordinary client SDK: $files permission already
  // allows any signed-in user to view a non-progress-photo file by path.
  const { data: fileData } = await db.queryOnce({
    $files: { $: { where: { path } } },
  })
  const url = fileData.$files?.[0]?.url || ''
  return { id: data?.id, url, path }
}

export async function websiteUsersWithoutPreferences() {
  const { data } = await db.queryOnce({
    $users: { preferences: {} },
  })
  return (data.$users || [])
    .filter((u: any) => !u.preferences)
    .map((u: any) => ({
      id: u.id,
      email: u.email || '',
      full_name: u.fullName || '',
      created_at: '',
      last_sign_in_at: '',
    }))
}

export async function countUsers() {
  const { data } = await db.queryOnce({ $users: {} })
  return (data.$users || []).length
}

/** Real coaching clients = userPreferences rows excluding admin accounts. */
export async function countClients() {
  const preferences = await fetchAllPreferences()
  return preferences.filter((p) => !isAdminEmail(p.email)).length
}

export async function countConsultationRequests() {
  const { data } = await db.queryOnce({ consultationRequests: {} })
  return (data.consultationRequests || []).length
}
